/**
 * Admin → Comms → SMS Test.
 *
 * Sends a one-off SMS through the comms service (sm4rt_emailer → TouchSMS) so
 * an operator can confirm the pipeline without a ticket or workflow behind it.
 *
 * The recipient is either typed in or picked from the customer index (which
 * shows the customer's mobile). The composer counts GSM-7 septets and parts as
 * you type — each part is another credit — and blocks emoji/smart quotes,
 * which would force UCS-2 encoding and halve the characters per credit.
 * The server re-validates everything; this is fast feedback, not the gate.
 */

import React, { useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Snackbar, TextField, Typography } from '@mui/material';
import { IconAddressBook, IconDeviceMobileMessage, IconSend, IconX } from '@tabler/icons-react';
import type { CustomerIndexItemDto } from '@techgitter/contracts';

import { CustomerLookupAutocomplete } from '../components/check-in/CustomerLookupAutocomplete';
import { Chip } from '../components/chip';
import { RadioOption } from '../components/panel/RadioOption';
import { sendTestSms, type SendTestSmsResult } from '../services/smsTestApi';
import { CARD_SX, PRIMARY_BTN_SX } from '../styles/designSystem';
import { formatAuMobile, normaliseAuMobile } from '../utils/auPhone';
import { bodyError, measureBody, MAX_BODY_LENGTH } from '../utils/smsText';
import './SmsTestAdminPage.css';

type RecipientMode = 'number' | 'customer';

export const SmsTestAdminPage: React.FC = () => {
  const [mode, setMode] = useState<RecipientMode>('number');
  const [typedNumber, setTypedNumber] = useState('');
  const [customer, setCustomer] = useState<CustomerIndexItemDto | null>(null);
  const [message, setMessage] = useState('');
  const [dryRun, setDryRun] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<SendTestSmsResult | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // The raw number for the active mode — what actually gets sent.
  const rawRecipient = mode === 'number' ? typedNumber : customer?.mobile ?? '';
  const normalised = useMemo(() => normaliseAuMobile(rawRecipient), [rawRecipient]);
  const recipientTouched = rawRecipient.trim().length > 0;
  const recipientInvalid = recipientTouched && !normalised;

  const stats = useMemo(() => measureBody(message), [message]);
  const messageError = useMemo(() => bodyError(message), [message]);

  const canSend = Boolean(normalised) && message.trim().length > 0 && !messageError && !sending;

  const handlePickCustomer = (picked: CustomerIndexItemDto) => {
    setCustomer(picked);
    setResult(null);
  };

  const handleSend = async () => {
    if (!canSend || !normalised) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setSending(true);
    setResult(null);
    try {
      const sent = await sendTestSms({ to: normalised, body: message, dryRun }, controller.signal);
      setResult(sent);
      setToast({
        message: sent.dryRun
          ? 'Message validated — nothing was sent (dry run).'
          : `SMS sent to ${formatAuMobile(sent.to)}.`,
        severity: 'success',
      });
    } catch (e) {
      if ((e as Error).name === 'AbortError') return;
      setToast({ message: (e as Error).message, severity: 'error' });
    } finally {
      setSending(false);
    }
  };

  const partsTone = stats.parts > 1 ? 'warning' : 'neutral';

  return (
    <Box className="sms-test">
      {/* ---------------------------------------------------------------- */}
      {/* Recipient                                                         */}
      {/* ---------------------------------------------------------------- */}
      <Box sx={[CARD_SX, { p: 2 }]}>
        <Typography className="sms-test__card-title">Recipient</Typography>

        <Box className="radio-option-group radio-option-group--row sms-test__modes">
          <RadioOption
            name="sms-recipient-mode"
            value="number"
            checked={mode === 'number'}
            onChange={() => { setMode('number'); setResult(null); }}
            icon={<IconDeviceMobileMessage size={15} stroke={1.5} />}
            label="Enter a number"
          />
          <RadioOption
            name="sms-recipient-mode"
            value="customer"
            checked={mode === 'customer'}
            onChange={() => { setMode('customer'); setResult(null); }}
            icon={<IconAddressBook size={15} stroke={1.5} />}
            label="Choose a customer"
          />
        </Box>

        {mode === 'number' ? (
          <TextField
            label="Mobile number"
            value={typedNumber}
            onChange={e => { setTypedNumber(e.target.value); setResult(null); }}
            placeholder="0412 345 678"
            size="small"
            fullWidth
            error={recipientInvalid}
            helperText={
              recipientInvalid
                ? 'Not a valid Australian mobile — SMS can only reach 04xx numbers.'
                : ' '
            }
            className="sms-test__field"
          />
        ) : (
          <Box className="sms-test__customer">
            <CustomerLookupAutocomplete onPick={handlePickCustomer} />
            {customer && (
              <Box className="sms-test__customer-picked">
                <span className="sms-test__customer-name">{customer.displayName}</span>
                {customer.mobile ? (
                  <Chip tone={normalised ? 'info' : 'error'}>
                    {normalised ? formatAuMobile(normalised) : customer.mobile}
                  </Chip>
                ) : (
                  <Chip tone="error">No mobile on file</Chip>
                )}
              </Box>
            )}
          </Box>
        )}

        {normalised && (
          <Typography className="sms-test__resolved">
            Sending to <strong>{normalised}</strong>
          </Typography>
        )}
      </Box>

      {/* ---------------------------------------------------------------- */}
      {/* Message                                                           */}
      {/* ---------------------------------------------------------------- */}
      <Box sx={[CARD_SX, { p: 2 }]}>
        <Typography className="sms-test__card-title">Message</Typography>

        <TextField
          value={message}
          onChange={e => { setMessage(e.target.value); setResult(null); }}
          placeholder="Plain text only — no emoji."
          multiline
          minRows={4}
          maxRows={12}
          fullWidth
          error={Boolean(messageError)}
          className="sms-test__field sms-test__message"
          inputProps={{ maxLength: MAX_BODY_LENGTH }}
        />

        <Box className="sms-test__meter">
          <span className="sms-test__count">
            {stats.length} character{stats.length === 1 ? '' : 's'}
          </span>
          <span className="sms-test__meter-sep">·</span>
          <Chip tone={partsTone}>
            {stats.parts} part{stats.parts === 1 ? '' : 's'}
          </Chip>
          {stats.parts > 0 && (
            <span className="sms-test__remaining">
              {stats.remainingInPart} left in this part
            </span>
          )}
          {stats.parts > 1 && (
            <span className="sms-test__cost-note">
              Each part costs a separate credit.
            </span>
          )}
        </Box>

        {messageError && (
          <Box className="sms-test__error">
            <IconX size={13} stroke={1.5} />
            <span>{messageError}</span>
          </Box>
        )}
      </Box>

      {/* ---------------------------------------------------------------- */}
      {/* Action                                                            */}
      {/* ---------------------------------------------------------------- */}
      <Box sx={[CARD_SX, { p: 2 }]} className="sms-test__actions">
        <label className={`radio-option sms-test__dry${dryRun ? ' radio-option--selected' : ''}`}>
          <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} />
          <span className="radio-option__text">
            <span className="radio-option__label">Dry run</span>
            <span className="radio-option__desc">Validate and cost the message without delivering it.</span>
          </span>
        </label>

        <Box className="sms-test__actions-spacer" />

        <Button
          variant="contained"
          sx={PRIMARY_BTN_SX}
          disabled={!canSend}
          onClick={handleSend}
          startIcon={
            sending ? <CircularProgress size={13} color="inherit" /> : <IconSend size={15} stroke={1.5} />
          }
        >
          {dryRun ? 'Validate' : 'Send SMS'}
        </Button>
      </Box>

      {result && (
        <Box sx={[CARD_SX, { p: 2 }]} className="sms-test__result">
          <Typography className="sms-test__card-title">
            {result.dryRun ? 'Validation result' : 'Sent'}
          </Typography>
          <Box className="sms-test__result-grid">
            <div className="sms-test__fact">
              <span className="sms-test__fact-label">To</span>
              <span className="sms-test__fact-value">{result.to}</span>
            </div>
            <div className="sms-test__fact">
              <span className="sms-test__fact-label">From</span>
              <span className="sms-test__fact-value">{result.from ?? '—'}</span>
            </div>
            <div className="sms-test__fact">
              <span className="sms-test__fact-label">Parts</span>
              <span className="sms-test__fact-value">{result.parts ?? '—'}</span>
            </div>
            <div className="sms-test__fact">
              <span className="sms-test__fact-label">Credits used</span>
              <span className="sms-test__fact-value">{result.credits ?? '—'}</span>
            </div>
            <div className="sms-test__fact">
              <span className="sms-test__fact-label">Credit balance</span>
              <span className="sms-test__fact-value">{result.creditBalance ?? '—'}</span>
            </div>
          </Box>
        </Box>
      )}

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default SmsTestAdminPage;
